<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import { onMounted } from "vue";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import api from "@/modules/api";
import i18n from "@/locales";
import {
  text2GalGameStory,
  text2GameStory,
} from "@/modules/editor/editor-json-utils";
import { ApiPostStoryGameDialogCreateMany } from "@/apis/story-game";
import { ApiPostGalGameDialogCreateMany } from "@/apis/gal-game";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { sessionStorageGet } from "@/tools/sessionStorage";
// State
const route = useRoute();
const store = useStory();
const router = useRouter();
const selectedStory = ref<Story | null>(null);
const loading = ref(false);

const showSplitModal = ref(false);
const splitSymbol = ref("。");
const showImgUploadPopup = ref(false);
const isPreview = ref(false);

let isEdit = false;

onMounted(async () => {
  loading.value = true;
  const storyUuid = route.query.story as string;
  try {
    if (storyUuid) {
      selectedStory.value = await store.getStoryItem(storyUuid);
      try {
        const list: any[] = [];
        const { content } = JSON.parse(selectedStory.value.text);
        for (const item of content) {
          if (
            item.type === "paragraph" &&
            Array.isArray(item.content) &&
            item.content[0].type === "text"
          ) {
            list.push(item);
          }
          if (item.type === "editorImage") {
            list.push(item);
          }
        }
        store.img.contentList = list;
        showSplitModal.value = true;
      } catch (e) {
        let splitList: any = [];
        selectedStory.value.text.split(splitSymbol.value).forEach(
          (v) =>
            v.trim() && {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: v.trim() + splitSymbol.value,
                },
              ],
            }
        );
        store.img.contentList = splitList;
      }
    } else {
      const textList: string[] = sessionStorageGet("text2GameList");
      if (!textList || !textList.length) return;
      const list: any[] = [];
      for (let text of textList) {
        text = text.trim();
        list.push({
          type: "paragraph",
          content: [
            {
              type: "text",
              text: text,
            },
          ],
        });
      }
      store.img.contentList = list;
    }
  } finally {
    loading.value = false;
  }
});

const clickConfirmSplit = () => {
  let splitList: any = [];

  store.img.contentList.forEach((v) => {
    if (v.type === "editorImage") splitList.push(v);
    else {
      v.content[0].text
        .split(splitSymbol.value)
        .forEach((v: string, i: number, arr: string[]) => {
          if (v.trim()) {
            // 处理 。“ 。”
            if (v === "”" || v === "“") return;
            let ext = arr.length - 1 === i ? "" : splitSymbol.value;
            if ((arr[i + 1] === "”" || arr[i + 1] === "“") && ext === "。")
              ext = "。”";

            splitList.push({
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: v + ext,
                },
              ],
            });
          }
        });
    }
  });

  store.img.contentList = splitList;
  showSplitModal.value = false;
};

const clickDeleteImg = (idx: number) => {
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.promptTitle"),
    message: i18n.global.t("ConfirmModal.sureDeleteImageText"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  })
    .then(() => {
      store.img.contentList.splice(idx, 1);
    })
    .catch(() => {});
};

const onMediaSelected = ([media]: any) => {
  store.img.contentList.splice(store.img.activeIdx, 0, {
    type: "editorImage",
    attrs: {
      src: media.url,
      alt: "",
    },
  });
  showImgUploadPopup.value = false;
};

const onGenerateImg = async (idx: number) => {
  let attrs: any;
  const { contentList, custom } = store.img;
  if (!isEdit)
    contentList.splice(idx, 0, {
      type: "editorImage",
      attrs: {
        src: "",
        alt: "1",
      },
    });
  else {
    attrs = contentList[idx].attrs;
    if (attrs.src === "") return;
    attrs.src = "";
  }
  if (!attrs) attrs = contentList[idx].attrs;
  let { prompt, traitId, style } = custom;
  const { text } = contentList[idx + 1].content[0];
  try {
    if (prompt === "") {
      const res = await api.storyPromptExtract({ text });
      prompt = res.data.prompt;
    }
    store.img.cache.traitId = traitId;
    store.img.cache.style = style;
    const { item } = await api.storyImageGeneration({
      prompt,
      traitId: traitId ? traitId : undefined,
      text,
      imageStyle: style ? style : undefined,
    });
    attrs.src = item.url;
    attrs.id = item.id;
    attrs.alt = [1, prompt, traitId, style, item.id].join("$$$$");
  } catch (e) {
    contentList.splice(idx, 1);
  }
};

const clickQuickImg = (idx: number, _isEdit: boolean) => {
  isEdit = _isEdit;
  store.img.activeIdx = idx;
  store.initImgModal();
  onGenerateImg(idx);
};

const clickCustomImg = (idx: number, _isEdit: boolean) => {
  isEdit = _isEdit;
  store.img.activeIdx = idx;
  store.initImgModal();
  store.img.showCustomModal = true;
  onCustomGeneratePrompt(true);
};

const clickSave = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.saving"),
  });
  try {
    await api.updateOneStory(selectedStory.value!.id!, {
      text: JSON.stringify({
        type: "doc",
        content: store.img.contentList,
      }),
    });
    await store.fetchStoryItem(selectedStory.value!.uuid);
    showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
    successModal.value = true;
  } catch (e) {
    showFailToast({ message: i18n.global.t("Message.saveFailed") });
  } finally {
    loading.close();
  }
};

const onCustomGenerate = () => {
  onGenerateImg(store.img.activeIdx);
  store.img.showCustomModal = false;
};

const onCustomGeneratePrompt = async (showLoading: boolean) => {
  const { activeIdx, contentList } = store.img;
  const { text } = contentList[activeIdx].content[0];
  showLoading && (store.img.promptLoading = true);
  if (!store.img.custom.prompt) {
    const res = await api.storyPromptExtract({ text });
    store.img.custom.prompt = res.data.prompt;
  }
  store.img.promptLoading = false;
};

const successModal = ref(false);

const clickCreateVisualNovel = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.creatingStorySceneTask"),
  });
  const { data } = await ApiPostStoryGameDialogCreateMany(
    text2GameStory(selectedStory.value!.id!, store.img.contentList)
  );
  const { id, storyGameId, storyGame } = data;
  selectedStory.value!.storyGameId = storyGameId;
  selectedStory.value!.storyGameUuid = storyGame.uuid;
  selectedStory.value!.storyGameStoryNodeId = id;
  router.push({
    name: "storySceneGeneration",
    query: {
      storyGameUuid: storyGame.uuid,
      storyUuid: selectedStory.value!.uuid,
      storyNodeId: id,
      storyGameId,
    },
  });
  loading.close();
};

// 游戏导入逻辑
const storyNodeId = ref(Number(route.query.storyNodeId));
const characterId = ref(Number(route.query.characterId));
const gameStore = useGalGameEditor();
const clickGameConfirm = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.importing"),
  });
  await ApiPostGalGameDialogCreateMany(
    text2GalGameStory(
      storyNodeId.value,
      characterId.value,
      store.img.contentList
    )
  );
  await gameStore.initDialog(gameStore.activeNode.id!, true);
  loading.close();
  router.back();
};

// 合并的逻辑
const clickMerge = (idx: number) => {
  const t = store.img.contentList.splice(idx, 1);
  store.img.contentList[idx - 1].content[0].text += t[0].content[0].text;
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav
      :enable-switch-mate="false"
      :show-back="true"
      :show-center="false"
    >
      <template #right>
        <button
          v-if="!storyNodeId"
          class="btn btn-sm rounded"
          @click="isPreview = !isPreview"
        >
          {{ isPreview ? $t("Buttons.exitPreview") : $t("Buttons.preview") }}
        </button>
        <button
          v-if="!isPreview"
          class="btn btn-sm rounded"
          @click="showSplitModal = true"
        >
          {{ $t("Buttons.splitSettings") }}
        </button>
        <button
          v-if="storyNodeId"
          class="btn btn-primary btn-sm rounded"
          @click="clickGameConfirm"
        >
          {{ $t("Buttons.confirm") }}
        </button>
        <button
          v-else
          class="btn btn-primary btn-sm rounded"
          @click="clickSave"
        >
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </threads-nav>
    <div class="w-full h-full overflow-auto p-2 flex flex-col gap-4">
      <div v-for="(item, idx) in store.img.contentList">
        <template v-if="item.type === 'paragraph'">
          <!-- 文本和文本之间的三个加图按钮 -->
          <div
            v-if="
              !isPreview &&
              (store.img.contentList[idx - 1]?.type === 'paragraph' ||
                idx === 0)
            "
            class="flex justify-center gap-2 my-2 flex-wrap"
          >
            <button
              class="btn btn-sm rounded flex items-center"
              @click="(store.img.activeIdx = idx), (showImgUploadPopup = true)"
            >
              <i class="iconify mingcute--pic-line1" />
              <p>{{ $t("Buttons.chooseImage") }}</p>
            </button>
            <button
              class="btn btn-sm rounded flex items-center"
              @click="clickQuickImg(idx, false)"
            >
              <i class="iconfont icon-paint" />
              <p>{{ $t("Buttons.quickGenerate") }}</p>
            </button>
            <button
              class="btn btn-sm rounded flex items-center"
              @click="clickCustomImg(idx, false)"
            >
              <i class="iconfont icon-paint" />
              <p>{{ $t("Buttons.customGenerate") }}</p>
            </button>
            <button
              v-if="
                idx !== 0 &&
                store.img.contentList[idx - 1]?.type === 'paragraph'
              "
              class="btn btn-sm rounded flex items-center"
              @click="clickMerge(idx)"
            >
              <i class="iconfont icon-merged" />
              <p>{{ $t("Buttons.merge") }}</p>
            </button>
          </div>
          <!-- 文本 -->
          <div>
            <p>{{ item.content[0].text }}</p>
          </div>
        </template>

        <div
          v-if="item.type === 'editorImage'"
          class="flex flex-col items-center justify-center"
        >
          <BaseImg
            v-if="item.attrs.src"
            :key="item.attrs.src"
            :url="item.attrs.src"
            ratio="16:9"
          />
          <VanLoading
            v-else
            class="mt-2"
            color="#fff"
            size="20px"
            type="spinner"
          />
          <div v-if="!isPreview" class="mt-2">
            <template v-if="item.attrs.alt?.startsWith('1')">
              <button
                class="btn btn-sm rounded mr-2"
                @click="clickCustomImg(idx, true)"
              >
                <i class="iconify mingcute--edit-3-line" />
              </button>

              <button
                class="btn btn-sm rounded mr-2"
                @click="clickQuickImg(idx, true)"
              >
                <i class="iconfont icon-rotate-right" />
              </button>
            </template>

            <button class="btn btn-sm rounded" @click="clickDeleteImg(idx)">
              <i class="iconfont icon-delete" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <MediaSelectPopup
      v-model:show="showImgUploadPopup"
      :enable-go-image-gen="false"
      :max-count="1"
      :multiple="false"
      :upload-type="7"
      enable-use-avatar
      media-type="img"
      replace
      @on-media-selected="onMediaSelected"
    />
    <CustomerModal @update:generate="onCustomGenerate" />
    <VanDialog
      v-model:show="showSplitModal"
      :cancel-button-text="$t('Buttons.cancel')"
      :confirm-button-text="$t('Buttons.momentSave')"
      :title="$t('Titles.splitSettings')"
      show-cancel-button
      @confirm="clickConfirmSplit"
    >
      <VanField
        v-model="splitSymbol"
        :label="$t('story.separatorSymbol')"
        :placeholder="$t('story.enterSeparatorSymbol')"
        class="mt-5"
      />
      <p class="text-base-content/50 text-xs p-4">
        {{ $t("Texts.splitSettingText") }}
      </p>
    </VanDialog>
    <VanDialog v-model:show="successModal" :showConfirmButton="false">
      <div class="flex flex-col justify-center gap-1">
        <button
          class="btn btn-primary"
          @click="
            $router.push({
              name: 'videoGeneration',
              query: {
                storyUuid: selectedStory!.uuid,
              },
            })
          "
        >
          {{ $t("story.createVideo") }}
        </button>
        <button class="btn btn-primary" @click="clickCreateVisualNovel()">
          {{ $t("story.createVisualNovel") }}
        </button>
        <button class="btn btn-primary" @click="successModal = false">
          {{ $t("Buttons.cancel") }}
        </button>
      </div>
    </VanDialog>
  </div>
</template>
